import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { getUnitSubmission } from './api/fieldApi';
import { FaCheckCircle, FaExclamationCircle, FaCheckSquare, FaBuilding, FaCalendarAlt, FaTimesCircle, FaDownload, FaChevronDown, FaStickyNote, FaFilter } from 'react-icons/fa';

export default function FieldBuildingRun() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const maintenanceID = searchParams.get('maintenanceID') || '';
  const refreshParam = searchParams.get('refresh');

  const [runData, setRunData] = useState({
    building: {
      id: buildingId,
      name: 'Loading...',
      address: '',
      buildingId: '', // This will be the buildingId from MaintenanceInspections
    },
    stats: {
      totalUnitsInRun: 0,
      unitsCompleted: 0,
      unitsWithIssues: 0,
      unitsWithNoIssues: 0,
    },
    schedule: '',
    formLink: '',
    // Per-contract "special notes" from the maintenance hub (e.g. "Begin
    // with PH04 — important board member suite"). Surfaced near the
    // daily schedule so techs see it before walking units.
    contractNotes: '',
    contractType: '',
    units: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [selectedUnitData, setSelectedUnitData] = useState(null);
  const [isLoadingUnitData, setIsLoadingUnitData] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showScheduleAndStats, setShowScheduleAndStats] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadRun() {
      setIsLoading(true);
      try {
        const baseUrl = `${API_BASE_URL}/api/field/buildings/${encodeURIComponent(buildingId)}/run`;
        const url = maintenanceID
          ? `${baseUrl}?maintenanceID=${encodeURIComponent(maintenanceID)}`
          : baseUrl;

        const resp = await fetch(url);
        if (!resp.ok) {
          throw new Error(`Failed to load building run: ${resp.status}`);
        }
        const data = await resp.json();

        if (!cancelled) {
          setRunData({
            building: {
              ...(data.building || {
                id: buildingId,
                name: data.building?.name || 'Unknown Building',
                address: data.building?.address || '',
              }),
              // buildingId from MaintenanceInspections (returned by backend)
              buildingId: data.building?.buildingId || data.buildingId || '',
              type: data.building?.type || '',
              maintenanceType: data.building?.maintenanceType || '',
            },
            stats: data.stats || {
              totalUnitsInRun: data.units?.length || 0,
              unitsCompleted: data.units?.filter((u) => u.status === 'completed' || u.status === 'done').length || 0,
              unitsWithIssues: data.units?.filter((u) => u.recommendation === true).length || 0,
              unitsWithNoIssues:
                (data.units?.filter((u) => u.status === 'completed' || u.status === 'done').length || 0) -
                  (data.units?.filter((u) => u.recommendation === true).length || 0) || 0,
            },
            // Backend returns schedule as a single string on building; fall back to legacy fields if needed
            schedule:
              (data.building && typeof data.building.schedule === 'string' && data.building.schedule) ||
              (typeof data.schedule === 'string' && data.schedule) ||
              (typeof data.scheduleText === 'string' && data.scheduleText) ||
              '',
            contractNotes:
              (data.building && typeof data.building.contractNotes === 'string' && data.building.contractNotes) ||
              '',
            contractType:
              (data.building && typeof data.building.contractType === 'string' && data.building.contractType) ||
              '',
            // form_link can come from the building object or top-level
            formLink:
              (data.building &&
                typeof data.building.form_link === 'string' &&
                data.building.form_link) ||
              (data.building &&
                typeof data.building.formLink === 'string' &&
                data.building.formLink) ||
              (data.form_link && typeof data.form_link === 'string' && data.form_link) ||
              (data.formLink && typeof data.formLink === 'string' && data.formLink) ||
              '',
            units: Array.isArray(data.units) ? data.units : [],
          });
        }
      } catch (error) {
        console.error('[FieldBuildingRun] Failed to fetch run data:', error);
        if (!cancelled) {
          setRunData({
            building: {
              id: buildingId,
              name: 'Unable to load building run',
              address: '',
            },
            stats: {
              totalUnitsInRun: 0,
              unitsCompleted: 0,
              unitsWithIssues: 0,
              unitsWithNoIssues: 0,
            },
            schedule: '',
            formLink: '',
            contractNotes: '',
            contractType: '',
            units: [],
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadRun();

    return () => {
      cancelled = true;
    };
  }, [buildingId, maintenanceID]); // Removed refreshParam from dependencies - commented out for now

  // Clean up refresh parameter after loading - commented out for now
  // useEffect(() => {
  //   if (refreshParam) {
  //     // Remove the refresh parameter from URL after it's been used
  //     const newSearchParams = new URLSearchParams(searchParams);
  //     newSearchParams.delete('refresh');
  //     setSearchParams(newSearchParams, { replace: true });
  //   }
  // }, [refreshParam, searchParams, setSearchParams]);

  const stats = useMemo(() => {
    if (runData.stats && typeof runData.stats.unitsCompleted === 'number') {
      // Calculate missed from units array - use status field to match table logic
      const missed = runData.units.filter((u) => {
        const normalizedStatus = (u.status || '').toLowerCase();
        return normalizedStatus === 'missed';
      }).length;

      return {
        total: runData.stats.totalUnitsInRun || runData.units.length,
        completed: runData.stats.unitsCompleted,
        withIssues: runData.stats.unitsWithIssues,
        noIssues: runData.stats.unitsWithNoIssues,
        missed,
      };
    }

    const total = runData.units.length;
    const completed = runData.units.filter((u) => u.status === 'completed' || u.status === 'done').length;
    const withIssues = runData.units.filter((u) => u.recommendation === true).length;
    const noIssues = completed - withIssues;
    
    // Calculate missed from units array - use status field to match table logic
    const missed = runData.units.filter((u) => {
      const normalizedStatus = (u.status || '').toLowerCase();
      return normalizedStatus === 'missed';
    }).length;

    return { 
      total, 
      completed, 
      withIssues, 
      noIssues: Math.max(noIssues, 0),
      missed,
    };
  }, [runData]);

  // "Other" carries the technician's free-text size, so group by that instead
  // of lumping every custom size under a single "Other" bucket.
  const filterSizeLabel = (unit) => {
    const size = String(unit?.filterSize || '').trim();
    if (!size) return '';
    if (size.toLowerCase() === 'other') {
      return String(unit?.filterSizeOther || '').trim() || 'Other';
    }
    return size;
  };

  const filterSizeCounts = useMemo(() => {
    const counts = new Map();
    runData.units.forEach((u) => {
      const label = filterSizeLabel(u);
      if (!label) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [runData.units]);

  const unitsMissingFilterSize = useMemo(
    () => runData.units.filter((u) => !filterSizeLabel(u)).length,
    [runData.units]
  );

  const [unitSearch, setUnitSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    function handleResize() {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredUnits = useMemo(() => {
    if (!unitSearch.trim()) return runData.units;
    const query = unitSearch.trim().toLowerCase();
    return runData.units.filter((u) =>
      String(u.unitNumber).toLowerCase().includes(query)
    );
  }, [runData.units, unitSearch]);

  const sortedUnits = useMemo(() => {
    const units = [...filteredUnits];
    const { column, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    units.sort((a, b) => {
      let va;
      let vb;

      if (column === 'unit') {
        va = String(a.unitNumber).toLowerCase();
        vb = String(b.unitNumber).toLowerCase();
      } else if (column === 'date') {
        const daRaw = a.visitDate || a.date;
        const dbRaw = b.visitDate || b.date;
        const da = daRaw ? new Date(daRaw).getTime() : 0;
        const db = dbRaw ? new Date(dbRaw).getTime() : 0;
        va = da;
        vb = db;
      } else if (column === 'workerName') {
        va = String(a.workerName || '').toLowerCase();
        vb = String(b.workerName || '').toLowerCase();
      } else if (column === 'location') {
        va = String(a.location || '').toLowerCase();
        vb = String(b.location || '').toLowerCase();
      } else if (column === 'equipmentType') {
        va = String(a.equipmentType || '').toLowerCase();
        vb = String(b.equipmentType || '').toLowerCase();
      } else if (column === 'filterSize') {
        va = filterSizeLabel(a).toLowerCase();
        vb = filterSizeLabel(b).toLowerCase();
      } else if (column === 'status') {
        va = String(a.status || '').toLowerCase();
        vb = String(b.status || '').toLowerCase();
      } else if (column === 'issues') {
        // Sort by recommendation field (True = has issues, False/null = no issues)
        const ia = a.recommendation === true ? 'HAS ISSUES 🔴' : 'OK 🟢';
        const ib = b.recommendation === true ? 'HAS ISSUES 🔴' : 'OK 🟢';
        va = ia.toLowerCase();
        vb = ib.toLowerCase();
      } else {
        return 0;
      }

      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });

    return units;
  }, [filteredUnits, sortConfig]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const cellPadding = isMobile ? '0.5rem 0.2rem' : '1rem 0.85rem';
  const headerFontSize = isMobile ? '0.8rem' : '1rem';
  const bodyFontSize = isMobile ? '0.8rem' : '0.95rem';
  const unitNumberFontSize = isMobile ? '0.9rem' : '1.1rem';

  return (
    <div style={{ padding: isMobile ? '1rem 0.75rem 4.5rem 0.75rem' : '1.5rem 2rem 4.5rem 2rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/schedule')}
          style={{
            marginBottom: '1rem',
            background: 'none',
            border: 'none',
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          ← Back to buildings
        </button>

        {/* Header + schedule + stats block */}
        <div
          style={{
            marginBottom: '1.5rem',
            padding: '1rem 1.25rem',
            borderRadius: '0.9rem',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            position: 'relative',
          }}
        >
          <button
            onClick={async () => {
              if (isDownloading) return;
              setIsDownloading(true);
              try {
                const url = `${API_BASE_URL}/api/field/buildings/${encodeURIComponent(buildingId)}/download`;
                const params = new URLSearchParams();
                if (maintenanceID) {
                  params.append('maintenanceID', maintenanceID);
                }
                if (runData.building?.name) {
                  params.append('buildingName', runData.building.name);
                }
                const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
                const resp = await fetch(fullUrl, { method: 'GET' });
                if (!resp.ok) {
                  throw new Error(`Download failed: ${resp.status}`);
                }
                const blob = await resp.blob();
                const cd = resp.headers.get('content-disposition') || '';
                const match = cd.match(/filename[^;=\n]*=(["']?)([^"';\n]+)\1/);
                const filename = match ? match[2] : `${(runData.building?.name || 'Building').replace(/\s+/g, '_')}_${maintenanceID || 'report'}_Maintenance.xlsx`;
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(a.href);
              } catch (error) {
                console.error('[FieldBuildingRun] Download error:', error);
              } finally {
                setIsDownloading(false);
              }
            }}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1.25rem',
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#374151',
              transition: 'background-color 0.15s ease, border-color 0.15s ease',
              opacity: isDownloading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            title="Download"
            disabled={isDownloading}
          >
            {isDownloading ? (
              <div style={{ fontSize: '0.8rem', color: '#6b7280', padding: '0 0.25rem' }}>
                ...
              </div>
            ) : (
              <FaDownload style={{ fontSize: '1rem' }} />
            )}
          </button>
          <div style={{ marginBottom: '1rem' }}>
            <h1
              style={{
                fontSize: '1.6rem',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <FaBuilding style={{ fontSize: '1.4rem', color: '#6b7280' }} />
              {runData.building.name}
            </h1>
            <p style={{ fontSize: '0.95rem', color: '#374151', margin: 0 }}>
              In Suite Maintenance
            </p>
            {runData.building.maintenanceType && (
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                Type: {runData.building.maintenanceType}
              </p>
            )}
          </div>

          {/* Collapsible Schedule and Stats Section */}
          <div>
            <button
              onClick={() => setShowScheduleAndStats(!showScheduleAndStats)}
              style={{
                width: '100%',
                background: 'none',
                border: '1px solid #e5e7eb',
                padding: '0.75rem 0.9rem',
                borderRadius: '0.75rem',
                backgroundColor: '#f9fafb',
                marginBottom: showScheduleAndStats ? '1rem' : 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
            >
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FaCalendarAlt style={{ fontSize: '1rem', color: '#6b7280' }} />
                Schedule & Stats
              </div>
              <FaChevronDown
                style={{
                  fontSize: '0.9rem',
                  color: '#6b7280',
                  transition: 'transform 0.2s ease',
                  transform: showScheduleAndStats ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </button>

            {showScheduleAndStats && (
              <>
                {/* Special notes — the per-contract notes the maintenance
                    hub keeps for this building's contract. Rendered above
                    the daily schedule so techs see access info, board
                    suites, etc. before walking units. Hidden when empty
                    so we don't add a noisy "no notes" card. */}
                {runData.contractNotes && runData.contractNotes.trim() ? (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.75rem 0.9rem',
                      borderRadius: '0.75rem',
                      backgroundColor: '#fef3c7',
                      border: '1px solid #fcd34d',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: '#78350f',
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <FaStickyNote style={{ fontSize: '1rem', color: '#b45309' }} />
                      Special Notes
                    </div>
                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: '#78350f',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                      }}
                    >
                      {runData.contractNotes}
                    </div>
                  </div>
                ) : null}

                {/* Daily schedule */}
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 0.9rem',
                    borderRadius: '0.75rem',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <FaCalendarAlt style={{ fontSize: '1rem', color: '#6b7280' }} />
                    Daily Schedule
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: '#4b5563',
                      whiteSpace: 'pre-wrap', // Preserves newlines and wraps text
                    }}
                  >
                    {typeof runData.schedule === 'string'
                      ? runData.schedule
                      : ''}
                  </div>
                </div>

                {/* Summary stats */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: '0.75rem',
                  }}
                >
            {[
              { label: 'Completed', value: stats.completed, key: 'completed' },
              { label: 'Issues', value: stats.withIssues, key: 'issues' },
              { label: 'No Issues', value: stats.noIssues, key: 'noIssues' },
              { label: 'Missed', value: stats.missed || 0, key: 'missed' },
            ].map((card) => {
              let accentColor = '#111827';
              let accentBg = '#f9fafb';

              if (card.key === 'completed') {
                accentColor = '#1d4ed8'; // blue
                accentBg = '#dbeafe';
              } else if (card.key === 'issues') {
                accentColor = '#b91c1c'; // red
                accentBg = '#fee2e2';
              } else if (card.key === 'noIssues') {
                accentColor = '#15803d'; // green
                accentBg = '#dcfce7';
              } else if (card.key === 'missed') {
                accentColor = '#ca8a04'; // yellow
                accentBg = '#fef9c3';
              }

              // Choose icon based on card type
              let IconComponent;
              if (card.key === 'completed') {
                IconComponent = FaCheckCircle;
              } else if (card.key === 'issues') {
                IconComponent = FaExclamationCircle;
              } else if (card.key === 'missed') {
                IconComponent = FaTimesCircle;
              } else {
                IconComponent = FaCheckSquare;
              }

              return (
                <div
                  key={card.label}
                  style={{
                    padding: '0.65rem 0.9rem',
                    borderRadius: '0.75rem',
                    backgroundColor: accentBg,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 700, 
                    color: accentColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    textAlign: 'left',
                    lineHeight: 1.2,
                  }}>
                    <IconComponent style={{ fontSize: '1.1rem' }} />
                    {card.value}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: 4, fontWeight: 500, textAlign: 'left' }}>
                    {card.label}
                  </div>
                </div>
              );
            })}
                </div>

                {/* Filter sizes used on this run */}
                {filterSizeCounts.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                      }}
                    >
                      <FaFilter style={{ fontSize: '0.75rem', color: '#6b7280' }} />
                      Filter Sizes
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile
                          ? 'repeat(2, minmax(0, 1fr))'
                          : 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: '0.5rem',
                      }}
                    >
                      {filterSizeCounts.map((row) => (
                        <div
                          key={row.label}
                          style={{
                            padding: '0.5rem 0.7rem',
                            borderRadius: '0.6rem',
                            backgroundColor: '#f9fafb',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                            {row.count}
                          </div>
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: '#4b5563',
                              marginTop: 2,
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.label}
                          >
                            {row.label}
                          </div>
                        </div>
                      ))}
                    </div>
                    {unitsMissingFilterSize > 0 && (
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.4rem' }}>
                        {unitsMissingFilterSize} unit{unitsMissingFilterSize === 1 ? '' : 's'} without a filter size recorded
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Table search */}
        <div
          style={{
            marginBottom: '0.75rem',
          }}
        >
          <input
            type="text"
            placeholder="Search unit #"
            value={unitSearch}
            onChange={(e) => setUnitSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem',
              borderRadius: '999px',
              border: '1px solid #d1d5db',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              outline: 'none',
            }}
          />
        </div>

        {/* Loading spinner */}
        {isLoading && (
          <div
            style={{
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinner animation="border" role="status" size={isMobile ? 'sm' : undefined}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        {/* Units table */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.75rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: isMobile ? 'fixed' : 'auto' }}>
            <thead>
              <tr
                style={{
                  backgroundColor: '#374151',
                  textAlign: 'left',
                  fontSize: headerFontSize,
                }}
              >
                {[
                  { key: 'unit', label: 'Unit', width: isMobile ? '10%' : 'auto' },
                  { key: 'date', label: 'Date', width: isMobile ? '14%' : 'auto' },
                  { key: 'workerName', label: 'Worker', width: isMobile ? '16%' : 'auto' },
                  { key: 'location', label: 'Location', width: isMobile ? '14%' : 'auto' },
                  { key: 'equipmentType', label: 'Equip', width: isMobile ? '14%' : 'auto' },
                  // Filter size is desktop-only: the mobile table is already at
                  // its width budget for techs working on a phone.
                  ...(isMobile ? [] : [{ key: 'filterSize', label: 'Filter', width: 'auto' }]),
                  { key: 'status', label: 'Status', width: isMobile ? '16%' : 'auto' },
                  { key: 'issues', label: 'Issues', width: isMobile ? '16%' : 'auto' },
                ].map((col) => (
                  <th
                    key={col.key}
                    style={{
                      padding: cellPadding,
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: isMobile && (col.key === 'location' || col.key === 'workerName' || col.key === 'equipmentType') ? 'normal' : 'nowrap',
                      width: col.width,
                      maxWidth: isMobile && col.key === 'location' ? '60px' : 'none',
                      color: '#ffffff',
                      fontWeight: 600,
                      borderBottom: '1px solid #4b5563',
                    }}
                    onClick={() => handleSort(col.key)}
                  >
                    <span>
                      {col.label}
                      {sortConfig.column === col.key && (
                        <span style={{ marginLeft: 4 }}>
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </span>
                    </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedUnits.map((u, index) => {
                // Use recommendation field to determine if unit has issues
                const hasIssues = u.recommendation === true;
                const issuesText = hasIssues ? 'HAS ISSUES 🔴' : 'OK 🟢';
                const normalizedStatus = (u.status || '').toLowerCase();
                const isDone = normalizedStatus === 'completed' || normalizedStatus === 'done';
                const isMissed = normalizedStatus === 'missed';

                const statusColor = isMissed
                  ? '#dc2626'
                  : isDone
                  ? '#16a34a'
                  : normalizedStatus === 'in_progress'
                  ? '#d97706'
                  : '#6b7280';

                const statusBg = isMissed
                  ? '#fee2e2'
                  : isDone
                  ? '#dcfce7'
                  : normalizedStatus === 'in_progress'
                  ? '#fef3c7'
                  : '#e5e7eb';
                const rowBg = index % 2 === 0 ? '#ffffff' : '#f3f7ff';

                return (
                  <tr 
                    key={u.id} 
                    style={{ 
                      fontSize: bodyFontSize, 
                      color: '#111827',
                      cursor: 'pointer',
                    }}
                    onClick={async (e) => {
                      // Fetch and show unit data
                      if (u.id) {
                        setIsLoadingUnitData(true);
                        setShowUnitModal(true);
                        try {
                          const unitData = await getUnitSubmission(u.id);
                          setSelectedUnitData(unitData);
                        } catch (error) {
                          console.error('[FieldBuildingRun] Failed to load unit data:', error);
                          setSelectedUnitData(null);
                        } finally {
                          setIsLoadingUnitData(false);
                        }
                      }
                    }}
                  >
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        fontWeight: 600,
                        backgroundColor: rowBg,
                        fontSize: unitNumberFontSize,
                      }}
                    >
                      {u.unitNumber}
                    </td>
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: rowBg,
                        fontSize: isMobile ? '0.75rem' : bodyFontSize,
                      }}
                    >
                      {(() => {
                        const raw = u.visitDate || u.date;
                        if (!raw) return '—';
                        const d = new Date(raw);
                        if (Number.isNaN(d.getTime())) return '—';
                        return d.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        });
                      })()}
                    </td>
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: rowBg,
                        fontWeight: 300,
                        fontSize: isMobile ? '0.75rem' : bodyFontSize,
                        maxWidth: isMobile ? '60px' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                      }}
                      title={u.workerName || ''}
                    >
                      {u.workerName || '—'}
                    </td>
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: rowBg,
                        fontWeight: 300,
                        fontSize: isMobile ? '0.75rem' : bodyFontSize,
                        maxWidth: isMobile ? '50px' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                      }}
                      title={u.location || ''}
                    >
                      {u.location}
                    </td>
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: rowBg,
                        fontWeight: 300,
                        fontSize: isMobile ? '0.75rem' : bodyFontSize,
                        maxWidth: isMobile ? '60px' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal',
                      }}
                      title={u.equipmentType || ''}
                    >
                      {u.equipmentType || '—'}
                    </td>
                    {!isMobile && (
                      <td
                        style={{
                          padding: cellPadding,
                          borderTop: '1px solid #e5e7eb',
                          backgroundColor: rowBg,
                          fontWeight: 300,
                          fontSize: bodyFontSize,
                          whiteSpace: 'nowrap',
                        }}
                        title={filterSizeLabel(u) || ''}
                      >
                        {filterSizeLabel(u) || '—'}
                      </td>
                    )}
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: rowBg,
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? '0.7rem' : '0.8rem',
                          padding: isMobile ? '0.08rem 0.35rem' : '0.12rem 0.45rem',
                          borderRadius: '999px',
                          backgroundColor: statusBg,
                          color: statusColor,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isMissed ? 'Missed' : isDone ? 'Done' : normalizedStatus === 'in_progress' ? (isMobile ? 'In Prog' : 'In Progress') : u.status || '—'}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: cellPadding,
                        borderTop: '1px solid #e5e7eb',
                        color: hasIssues ? '#b91c1c' : '#16a34a',
                        backgroundColor: rowBg,
                        fontWeight: 500,
                        fontSize: isMobile ? '1.3rem' : bodyFontSize,
                        textAlign: isMobile ? 'center' : 'left',
                      }}
                    >
                      {isMobile ? (hasIssues ? '🔴' : '🟢') : issuesText}
                    </td>
                  </tr>
                );
              })}
              {sortedUnits.length === 0 && (
                <tr>
                  <td
                    colSpan={isMobile ? 7 : 8}
                    style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontSize: '0.9rem',
                      color: '#6b7280',
                      borderTop: '1px solid #e5e7eb',
                    }}
                  >
                    No units match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Add New Unit CTA */}
      <div
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'min(420px, 90%)',
          }}
        >
          <button
            type="button"
            style={{
              width: '100%',
              padding: '0.9rem 1.5rem',
              borderRadius: '999px',
              border: 'none',
              background:
                'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #4338ca 100%)',
              color: '#ffffff',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 10px 25px rgba(37, 99, 235, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              justifyContent: 'center',
            }}
            onClick={() => {
              const buildingName = runData.building?.name || '';
              const params = new URLSearchParams();
              if (maintenanceID) params.set('maintenanceID', maintenanceID);
              if (buildingName) params.set('buildingName', buildingName);
              const queryString = params.toString();
              const addUnitUrl = `/schedule/buildings/${buildingId}/add-unit${queryString ? `?${queryString}` : ''}`;
              navigate(addUnitUrl);
            }}
          >
            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>＋</span>
            <span>Add New Unit</span>
          </button>
        </div>

      {/* Unit Details Modal */}
      {showUnitModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => {
            setShowUnitModal(false);
            setSelectedUnitData(null);
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '0.75rem',
              width: '100%',
              maxWidth: '800px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                  Unit Details
                </h2>
                {selectedUnitData?.unitId && (
                  <button
                    type="button"
                    onClick={() => {
                      // Navigate to edit form
                      const buildingName = runData.building?.name || '';
                      const params = new URLSearchParams();
                      if (maintenanceID) params.set('maintenanceID', maintenanceID);
                      if (buildingName) params.set('buildingName', buildingName);
                      if (selectedUnitData.unitId) params.set('unitId', selectedUnitData.unitId);
                      const queryString = params.toString();
                      const editUrl = `/schedule/buildings/${buildingId}/add-unit${queryString ? `?${queryString}` : ''}`;
                      
                      // Close modal and navigate
                      setShowUnitModal(false);
                      setSelectedUnitData(null);
                      navigate(editUrl);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#2563eb',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #2563eb',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#dbeafe';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#eff6ff';
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowUnitModal(false);
                  setSelectedUnitData(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div
              style={{
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {isLoadingUnitData ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : selectedUnitData ? (
                <UnitDetailsDisplay unitData={selectedUnitData} />
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  Failed to load unit data.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Component to display unit details (similar to Step 5)
function UnitDetailsDisplay({ unitData }) {
  const step1Data = unitData.step1 || {};
  const step2Data = unitData.step2 || {};
  const step3Data = unitData.step3 || {};
  const step4Data = unitData.step4 || {};
  const isDryerEquipment = step2Data.equipmentType === 'Dryer / Bathroom Vent' || step2Data.equipmentType === 'dryer-bathroom-vent';
  const isERVEquipment = step2Data.equipmentType === 'ERV' || step2Data.equipmentType === 'erv';
  const dryerPhotoFields = [
    { key: 'beforePhotoLintTrap', label: 'Before Photo of Lint Trap' },
    { key: 'afterPhotoLintTrap', label: 'After Photo of Lint Trap' },
    { key: 'beforePhotoBoosterFan', label: 'Before Photo of Booster Fan' },
    { key: 'afterPhotoBoosterFan', label: 'After Photo of Booster Fan' },
    { key: 'beforePhotoBathroomFan', label: 'Before Photo of Bathroom Fan' },
    { key: 'afterPhotoBathroomFan', label: 'After Photo of Bathroom Fan' },
  ];

  // Helper to format array values
  const formatArray = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return 'None';
    return arr.join(', ');
  };

  // Helper to format file URLs
  const formatFiles = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return 'None';
    return `${arr.length} file(s) uploaded`;
  };

  // Component labels mapping
  const componentLabels = {
    thermostat: 'Thermostat',
    leakSensor: 'Leak Sensor',
    blowerFan: 'Blower Fan',
    actuator: 'Actuator',
    threeWayValve: '3-Way Valve',
    drainPan: 'Drain Pan',
    mould: 'Potential Mould',
    valves: 'Valves',
    hoses: 'Hoses',
    coils: 'Coils',
    insulation: 'Insulation',
    compressor: 'Compressor',
    dryerTurningOn: 'Dryer Turning On?',
    exhaustHoseCondition: 'Exhaust Hose Condition',
    boosterFanCondition: 'Booster Fan Condition',
    bathroomFanCondition: 'Bathroom Fan Condition',
    ervPanelCondition: 'ERV Panel Condition',
  };

  // Check if unit has issues and login_id
  const hasIssues = unitData.recommendation === true;
  const loginId = unitData.login_id;

  return (
    <div>
      {/* Yellow Slip Number Section - Only show if there are issues */}
      {hasIssues && loginId && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
            Yellow Slip Number
          </h3>
          <div
            style={{
              padding: '1.5rem',
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '0.75rem',
              borderLeft: '4px solid #f59e0b',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#2563eb',
                letterSpacing: '0.1em',
                fontFamily: 'monospace',
                textAlign: 'center',
                marginBottom: '0.5rem',
              }}
            >
              {loginId}
            </div>
            <p
              style={{
                fontSize: '0.875rem',
                color: '#92400e',
                textAlign: 'center',
                margin: 0,
              }}
            >
              Write this number on the yellow paper and give it to the resident or leave it somewhere visible
            </p>
          </div>
        </div>
      )}

      {/* Step 1 Data */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
          General Information
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#374151' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Field</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Unit Number</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.unitNumber || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Maintenance Date</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.maintenanceDate || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Worker Name</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.workerName || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Select Equipment</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.equipmentType || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Equipment Location</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.equipmentLocation || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Was the unit missed?</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.unitMissed || '—'}</td>
            </tr>
            {step1Data.unitMissed === 'Yes' && (
              <>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff', paddingLeft: '2.5rem' }}>Photo of Missed Unit</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step1Data.missedUnitPhoto)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff', paddingLeft: '2.5rem' }}>Reason Missed</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.missedUnitReason || '—'}</td>
                </tr>
              </>
            )}
            {step1Data.unitMissed !== 'Yes' && (
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Before Picture of Fan Coil</td>
                <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step2Data.beforeFanCoilPhoto)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Step 3 Data - Component Checks */}
      {step3Data && Object.keys(step3Data).length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
            Components Check
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#374151' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Component</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(componentLabels).map(([key, label], index) => {
                const status = step3Data[key];
                if (!status) return null;
                
                return (
                  <React.Fragment key={key}>
                    <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>{label}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{status}</td>
                    </tr>
                    {status === 'HAS ISSUES 🔴' && (
                      <>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                          <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Photo</td>
                          <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data[`${key}Photo`])}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                          <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Description</td>
                          <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data[`${key}Description`] || '—'}</td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              
              {/* Any Other Custom Issues */}
              {step3Data.anyOtherCustomIssues && (
                <>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Any Other Custom Issues</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.anyOtherCustomIssues}</td>
                  </tr>
                  {step3Data.anyOtherCustomIssues === 'HAS ISSUES 🔴' && (
                    <>
                      <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Photo</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.anyOtherCustomIssuesPhoto)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Description</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.anyOtherCustomIssuesDescription || '—'}</td>
                      </tr>
                    </>
                  )}
                </>
              )}

              {isDryerEquipment && (
                <>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Booster Fan Location</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.boosterFanLocation || '—'}</td>
                  </tr>
                  {dryerPhotoFields.map((field, index) => (
                    <tr
                      key={field.key}
                      style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}
                    >
                      <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>{field.label}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data[field.key])}</td>
                    </tr>
                  ))}
                </>
              )}

              {isERVEquipment && (
                <>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>ERV Type</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.ervType || '—'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Before Photo</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.ervBeforePhoto)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>After Photo</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.ervAfterPhoto)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Step 4 Data */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
          Extra Info
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr style={{ backgroundColor: '#374151' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Field</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Photo of Model Sticker Plate</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step4Data.modelStickerPlatePhoto)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Additional Photos</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step4Data.additionalPhotos)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Resident Email</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.residentEmail || '—'}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Additional Notes</td>
              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.additionalNotes || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}


